import React, { useMemo, useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useDropzone } from 'react-dropzone'
import formStyles from '../form.module.css'
import styles from './upload-file.module.css'
import Iconify from '../../../../Iconify'

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024

const DEFAULT_ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png']
}

const isImageFile = (file) => {
  const t = (file.type || '').toLowerCase()
  if (t.startsWith('image/')) return true
  return /\.(jpe?g|png)$/i.test(file.name || '')
}

const pickFileIcon = (file) => {
  if (!file || !(file instanceof File)) return 'solar:document-text-bold-duotone'
  if (isImageFile(file)) {
    return 'mdi:file-image-outline'
  }
  return 'mdi:file-outline'
}

const formatMaxMb = (bytes) => String(Math.round(bytes / (1024 * 1024)))

function UploadDropArea ({
  name,
  field,
  fieldState,
  label,
  helpText,
  required,
  accept,
  disabled,
  maxSize = DEFAULT_MAX_BYTES,
  chooseLabel = 'Elegir archivo',
  replaceHint = 'Toca para reemplazar',
  specsLine
}) {
  const [rejectHint, setRejectHint] = useState('')
  const acceptResolved = useMemo(
    () => (accept && Object.keys(accept).length ? accept : DEFAULT_ACCEPT),
    [accept]
  )

  const specsDefault = useMemo(() => {
    const mb = formatMaxMb(maxSize)
    return `PDF o imagen (JPG, PNG) · máx. ${mb} MB`
  }, [maxSize])

  const specs = specsLine || specsDefault

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptResolved,
    disabled,
    maxFiles: 1,
    maxSize,
    onDropAccepted: (files) => {
      setRejectHint('')
      if (files[0]) field.onChange(files[0])
    },
    onDropRejected: (rejections) => {
      const code = rejections[0]?.errors[0]?.code
      if (code === 'file-too-large') {
        setRejectHint('El archivo supera el tamaño máximo permitido.')
      } else {
        setRejectHint('Tipo de archivo no permitido.')
      }
    }
  })

  const file = field.value instanceof File ? field.value : null
  const hasFile = Boolean(file)

  const { ref: dropzoneInputRef, ...inputProps } = getInputProps()

  const mergeInputRef = (node) => {
    if (typeof dropzoneInputRef === 'function') {
      dropzoneInputRef(node)
    } else if (dropzoneInputRef != null) {
      dropzoneInputRef.current = node
    }
    field.ref(node)
  }

  const rootClass = [
    styles.dropRoot,
    hasFile ? styles.dropRootHasFile : '',
    isDragActive ? styles.dropRootDragging : '',
    fieldState.error ? styles.dropRootError : '',
    disabled ? styles.dropRootDisabled : ''
  ]
    .filter(Boolean)
    .join(' ')

  const rootProps = getRootProps({
    onBlur: field.onBlur,
    className: rootClass,
    'aria-invalid': fieldState.error ? 'true' : 'false',
    'aria-describedby': [
      fieldState.error ? `${name}-error` : null,
      helpText ? `${name}-help` : null,
      rejectHint ? `${name}-reject` : null
    ]
      .filter(Boolean)
      .join(' ') || undefined
  })

  return (
    <div className={formStyles.field}>
      <label className={formStyles.label} htmlFor={name}>
        {label}{' '}
        {required && <span className={formStyles.required}>*</span>}
      </label>

      <div {...rootProps}>
        <input
          {...inputProps}
          id={name}
          disabled={disabled}
          ref={mergeInputRef}
        />
        <span className={styles.iconWrap} aria-hidden>
          <Iconify
            icon={pickFileIcon(file)}
            style={{ fontSize: 28, color: '#97ff48' }}
          />
        </span>
        <div className={styles.textCol}>
          {hasFile ? (
            <>
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.sub}>{replaceHint}</span>
            </>
          ) : (
            <>
              <span className={styles.title}>{chooseLabel}</span>
              <span className={styles.sub}>{specs}</span>
            </>
          )}
        </div>
      </div>

      {fieldState.error ? (
        <p id={`${name}-error`} className={formStyles.error} role="alert">
          {fieldState.error.message}
        </p>
      ) : null}

      {rejectHint ? (
        <p id={`${name}-reject`} className={styles.reject} role="status">
          {rejectHint}
        </p>
      ) : null}

      {helpText ? (
        <p id={`${name}-help`} className={formStyles.helpText}>
          {helpText}
        </p>
      ) : null}
    </div>
  )
}

export const UploadFile = ({
  name = 'file',
  label = '',
  helpText = '',
  required = false,
  accept,
  disabled = false,
  maxSize = DEFAULT_MAX_BYTES,
  chooseLabel,
  replaceHint,
  specsLine
}) => {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      render={({ field, fieldState }) => (
        <UploadDropArea
          name={name}
          field={field}
          fieldState={fieldState}
          label={label}
          helpText={helpText}
          required={required}
          accept={accept}
          disabled={disabled}
          maxSize={maxSize}
          chooseLabel={chooseLabel}
          replaceHint={replaceHint}
          specsLine={specsLine}
        />
      )}
    />
  )
}
